import _extends from '@babel/runtime/helpers/esm/extends';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useRef, useEffect, useMemo } from 'react';
import { TextInput } from '@paypalcorp/pp-react-text-input';
import { CriticalIcon } from '@paypalcorp/pp-react-icons';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import { devWarning, KeyboardCode } from '@paypalcorp/pp-react-utils';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { textStyles } from '@paypalcorp/pp-react-text';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  // 40px
  var sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysSpaceSm = tokens.sysSpaceSm,
    sysTypographyFieldValueLgLineHeight = tokens.sysTypographyFieldValueLgLineHeight,
    sysSpaceXs = tokens.sysSpaceXs,
    sysSizeMediaSm = tokens.sysSizeMediaSm,
    sysSizeFieldSm = tokens.sysSizeFieldSm,
    sysColorErrorMain = tokens.sysColorErrorMain;
  var _textStyles = textStyles(tokens),
    headingSm = _textStyles.headingSm,
    caption = _textStyles.caption;
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: code_input_base;\n    display: inline-block;\n  "])));
  var codeInputTextInput = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: code_input_text_input;\n    width: ", ";\n    margin-right: ", "; // 8px, gap property doesn't work\n    margin-bottom: ", "; // 8px, gap property doesn't work\n    ", ";\n    @media (max-width: ", ") {\n      width: ", ";\n    }\n  "])), sysSizeFieldSm, sysSpaceSm, sysSpaceSm, headingSm(), sysSizeMediaSm, '2.5rem');
  var codeInputTextInputWithError = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: code_input_text_input_with_error;\n    padding: 0;\n  "])));
  var codeInputTextInputControl = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: code_input_text_input_control;\n    ", ";\n    text-align: center;\n    padding: 0;\n    caret-color: transparent; // change the blinking caret color in text input\n    -webkit-touch-callout: none; // iOS Safari disable text selection highlight\n\n    // hide counter buttons for type:number (safari/chrome)\n    &::-webkit-outer-spin-button,\n    &::-webkit-inner-spin-button {\n      -webkit-appearance: none;\n      margin: 0;\n    }\n    // hide counter buttons for type:number (firefox)\n    &[type='number'] {\n      -moz-appearance: textfield;\n    }\n\n    // remove text highlight\n    &::selection {\n      background: transparent;\n    }\n    &::-moz-selection {\n      background: transparent;\n    }\n\n    // overwrite to fix the issue with bottom text getting cropped on chrome\n    &:first-line {\n      line-height: ", ";\n    }\n  "])), headingSm(), sysTypographyFieldValueLgLineHeight);
  var inputWrapper = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: input_wrapper;\n    display: flex;\n    margin-right: -", "; // -8px to compensate spacing on input wrapper\n    margin-bottom: -", "; // -8px to compensate spacing on input wrapper\n    flex-wrap: wrap;\n    [dir='rtl'] & {\n      direction: ltr; // use ltr direction for input fields\n    }\n  "])), sysSpaceSm, sysSpaceSm);
  var error = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: error;\n    margin-top: ", ";\n    display: flex;\n    align-items: center;\n  "])), sysSpaceXs);
  var errorText = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: error_text;\n    ", ";\n    color: ", ";\n    flex-grow: 1;\n    width: 0;\n  "])), caption(), sysColorBackgroundMediumContrast);
  var errorIcon = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: error_icon;\n    color: ", ";\n    margin-right: ", ";\n    [dir='rtl'] & {\n      margin-right: 0;\n      margin-left: ", ";\n    }\n  "])), sysColorErrorMain, sysSpaceXs, sysSpaceXs);
  var errorTextList = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: error_text_list;\n    list-style: none;\n    padding: 0;\n    margin: 0;\n  "])));
  return {
    base: base,
    codeInputTextInput: codeInputTextInput,
    codeInputTextInputWithError: codeInputTextInputWithError,
    codeInputTextInputControl: codeInputTextInputControl,
    inputWrapper: inputWrapper,
    error: error,
    errorText: errorText,
    errorIcon: errorIcon,
    errorTextList: errorTextList
  };
}
var _excluded = ["alphaCharCapitalize", "className", "errorText", "error", "name", "id", "inputClassName", "inputWrapperClassName", "onChange", "pinLength", "value", "autoFocus", "onBlur", "onFocus", "numberOnly", "wrapperAriaLabel"];
var CodeInput = React.forwardRef(function (_ref, ref) {
  var _ref$alphaCharCapital = _ref.alphaCharCapitalize,
    alphaCharCapitalize = _ref$alphaCharCapital === void 0 ? false : _ref$alphaCharCapital,
    className = _ref.className,
    errorText = _ref.errorText,
    _ref$error = _ref.error,
    error = _ref$error === void 0 ? !!errorText : _ref$error,
    _ref$name = _ref.name,
    name = _ref$name === void 0 ? 'default' : _ref$name,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? name : _ref$id,
    inputClassName = _ref.inputClassName,
    inputWrapperClassName = _ref.inputWrapperClassName,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? devWarning('Required prop `onChange` is missing') : _ref$onChange,
    _ref$pinLength = _ref.pinLength,
    pinLength = _ref$pinLength === void 0 ? 6 : _ref$pinLength,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? '' : _ref$value,
    autoFocus = _ref.autoFocus,
    _ref$onBlur = _ref.onBlur,
    onBlur = _ref$onBlur === void 0 ? null : _ref$onBlur,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? null : _ref$onFocus,
    _ref$numberOnly = _ref.numberOnly,
    numberOnly = _ref$numberOnly === void 0 ? false : _ref$numberOnly,
    _ref$wrapperAriaLabel = _ref.wrapperAriaLabel,
    wrapperAriaLabel = _ref$wrapperAriaLabel === void 0 ? 'group' : _ref$wrapperAriaLabel,
    rest = _objectWithoutProperties(_ref, _excluded);
  var internalRef = useRef([]);
  var inputFieldRef = ref || internalRef; // pin length 3-6

  var MAX_PIN = 6;
  var NUM_REGEX = /^[0-9]+$/;
  var keyEventsArry = [KeyboardCode.NAME_BACKSPACE, KeyboardCode.NAME_DELETE, KeyboardCode.NAME_TAB];
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    focused = _React$useState2[0],
    setFocused = _React$useState2[1]; // check pin length and convert into array

  var pinArray = pinLength < 3 || pinLength >= MAX_PIN ? _toConsumableArray(Array(MAX_PIN).keys()) : _toConsumableArray(Array(pinLength).keys()); // convert it into an array and remove empty value

  var valueArr = value && value.split('').map(function (val) {
    return val === ' ' ? '' : val;
  }); // generate the final code

  var generateCode = function () {
    // generate the final code and add spaces for empty value
    var pinArr = inputFieldRef.current.map(function (input) {
      return input.value === '' ? ' ' : input.value;
    }).join(''); // onChange is called on each key

    if (onChange) {
      onChange(pinArr);
    }
  }; // focus on field provided an index valid

  var focusOnField = function (idx) {
    // check if idx is in the range

    /* istanbul ignore next */
    if (inputFieldRef && inputFieldRef.current[idx]) {
      inputFieldRef.current[idx].focus();
    }
  }; // process each character paste/input in each field

  var iterateEachCharacter = function (pastedArr, idx) {
    var fieldLength = inputFieldRef.current.length;
    var minArr = Math.min(pastedArr && pastedArr.length, fieldLength);
    var index = idx;
    for (var i = 0; i < minArr; i++) {
      inputFieldRef.current[index].value = alphaCharCapitalize ? pastedArr[i].toUpperCase() : pastedArr[i];
      index++; // break the loop if index is equal to length of the field

      if (index === fieldLength) {
        break;
      }
    } // focus on next field or focus on the last field

    focusOnField(index === fieldLength ? index - 1 : index);
  };
  var handleInput = function (evt, idx) {
    // move focus fwd if there is a value
    if (evt.target.value !== '') {
      focusOnField(idx + 1);
    } // capitalize the characters

    if (alphaCharCapitalize) {
      evt.target.value = evt.target.value.toUpperCase();
    } // if more than 1 characters split the values
    // iterate each character into single field.

    if (evt.target.value.length > 1) {
      var pastedArr = evt.target.value.replace(/\s/g, '').split('');
      iterateEachCharacter(pastedArr, idx);
    }
    generateCode();
  };
  useEffect(function () {
    if (autoFocus) {
      focusOnField(0);
    }
  }, []);
  /* eslint-disable complexity */

  var handleKeyDown = function (evt, idx) {
    /* istanbul ignore next */
    var keyCode = evt.key || evt.keyCode;
    var KeyCode = new KeyboardCode(keyCode);
    if (KeyCode.IS_SPACEBAR || KeyCode.IS_HOME || KeyCode.IS_END || KeyCode.IS_ARROW_KEY) {
      evt.preventDefault();
    }
    if (KeyCode.IS_BACKSPACE || KeyCode.IS_DELETE) {
      /* istanbul ignore next */
      if (evt.target.value === '' || evt.target.value === ' ') {
        focusOnField(idx - 1);
      } else {
        /* istanbul ignore next */
        evt.target.value = ' ';
        /* istanbul ignore next */

        generateCode();
      }
    } else if (KeyCode.IS_ARROW_LEFT) {
      focusOnField(idx - 1); // move focus back
    } else if (KeyCode.IS_ARROW_RIGHT) {
      focusOnField(idx + 1); // move focus forward
    } else if (KeyCode.IS_HOME) {
      focusOnField(0); // move focus back
    } else if (KeyCode.IS_END) {
      focusOnField(pinLength - 1); // move focus to last
    } else {
      evt.target.select(); // select the value so it can be replaced
    } // to allow paste command ctrl + v / cmd + v

    if ((evt.ctrlKey || evt.metaKey) && keyCode === 'v') {
      return;
    } // limit users to allow only numeric value

    if (!keyCode.match(NUM_REGEX) && !keyEventsArry.includes(keyCode) && numberOnly) {
      evt.preventDefault();
    }
  };
  /* eslint-enable complexity */
  // set ref of each input field and manually add the value
  // to make it a controlled component

  var setRef = function (el, idx) {
    if (el) {
      inputFieldRef.current[idx] = el;
      inputFieldRef.current[idx].value = valueArr ? valueArr[idx] : '';
    }
  };
  var handleBlur = function (event) {
    /* istanbul ignore else */
    if (onBlur && !inputFieldRef.current.includes(event.relatedTarget)) {
      onBlur(event);
      setFocused(false);
    }
  };
  var handleFocus = function (event) {
    /* istanbul ignore else */
    event.target.select();
    if (onFocus && !focused) {
      setFocused(true);
      onFocus(event);
    }
  }; // handle paste

  var handlePaste = function (evt, idx) {
    var clipboardValue = evt.clipboardData && evt.clipboardData.getData('text'); // ignore the default behavior, onPaste calls onChange

    evt.preventDefault();
    if (clipboardValue && !clipboardValue.match(NUM_REGEX) && numberOnly) {
      return;
    }
    var pastedArr = clipboardValue && clipboardValue.replace(/\s/g, '').split('');
    iterateEachCharacter(pastedArr, idx);
    generateCode();
  };
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var containerCSS = _styles.base;
  var loadInputField = pinArray.map(function (idx) {
    return jsx(ClassNames, {
      key: idx,
      "data-ppui": "true"
    }, function (_ref3) {
      var css = _ref3.css,
        cx = _ref3.cx;
      return jsx(TextInput, _extends({
        key: idx,
        className: cx(css([_styles.codeInputTextInput, errorText && _styles.error]), inputWrapperClassName),
        inputClassName: cx(css([_styles.codeInputTextInputControl, errorText && _styles.codeInputTextInputWithError]), inputClassName),
        "aria-label": "".concat(idx + 1, "-").concat(pinArray.length),
        name: "".concat(name, "-").concat(idx),
        id: "ci-".concat(id, "-").concat(idx),
        ref: function (el) {
          return setRef(el, idx);
        },
        onInput: function onInput(evt) {
          return handleInput(evt, idx);
        },
        onKeyDown: function onKeyDown(evt) {
          return handleKeyDown(evt, idx);
        },
        onPaste: function onPaste(evt) {
          return handlePaste(evt, idx);
        },
        onFocus: handleFocus,
        onBlur: handleBlur,
        "aria-describedby": error ? "message_".concat(name) : null,
        error: error && !!errorText,
        pattern: numberOnly ? '[0-9]*' : ''
      }, rest, {
        "data-ppui": "true"
      }));
    });
  });
  return jsx("div", {
    css: containerCSS,
    className: className,
    id: id,
    "data-ppui-info": "code-input_2.3.7"
  }, jsx("div", {
    css: _styles.inputWrapper,
    role: "group",
    "aria-label": wrapperAriaLabel,
    "data-ppui": "true"
  }, loadInputField), errorText && function loadError() {
    var processText = Array.isArray(errorText) ? jsx("ul", {
      css: _styles.errorTextList,
      "data-ppui": "true"
    }, errorText.map(function (text, index) {
      return jsx("li", {
        key: "text$-".concat(index),
        "data-ppui": "true"
      }, text);
    })) : errorText;
    return jsx(ClassNames, {
      "data-ppui": "true"
    }, function (_ref2) {
      var css = _ref2.css;
      return jsx("div", {
        css: _styles.error,
        id: "message_".concat(name),
        role: "alert",
        "data-ppui": "true"
      }, jsx(CriticalIcon // Refactor this with ClassName
      , {
        className: css(_styles.errorIcon),
        size: "sm",
        "aria-hidden": "true",
        "data-ppui": "true"
      }), jsx("span", {
        css: _styles.errorText,
        "data-ppui": "true"
      }, processText));
    });
  }());
});
export { CodeInput, styles as codeInputStyles };